import { useTranslation } from 'react-i18next';
import Input from '../Input/Input';
import { useFormik } from 'formik';
import Modal from '../Modal/Modal';
import { ChangeEvent, useEffect } from 'react';
import Button from '../Button';
import MiniLoader from '../MiniLoader/MiniLoader';

interface Props {
  isModalOpened: boolean;
  closeModal: () => void;
  onSubmit: (v: string) => Promise<void>;
  title: string;
  label: string;
  initialValue?: string;
}

interface FormValues {
  val: string;
}

export const COANumberModal = ({
  isModalOpened,
  closeModal,
  onSubmit,
  title,
  label,
  initialValue = '',
}: Props) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isModalOpened) {
      formik.setFieldValue('val', initialValue);
    }
    // eslint-disable-next-line
  }, [isModalOpened]);

  const formik = useFormik<FormValues>({
    initialValues: {
      val: initialValue,
    },
    validate: (values) => {
      const errors: Partial<FormValues> = {};

      if (values.val !== '' && +values.val <= 0) {
        errors.val = t('Number should be higher then 0 or empty');
      }

      return errors;
    },
    onSubmit: async (values) => {
      await onSubmit(values.val);
    },
  });

  return (
    <Modal
      name='coa-number'
      isOpened={isModalOpened}
      close={closeModal}
      title={t('Settings for the chart of accounts')}
    >
      <div className='mx-4 px-4 pb-6 pt-2'>
        <div className='mb-4 text-h4 font-extrabold'>{t(title)}</div>
        <div className='flex items-center gap-x-6'>
          <div className='max-w-[358px] grow pb-4'>{t(label)}</div>
          <div>
            <Input
              name='val'
              className={
                'max-w-[168px] !border-gray-500 !px-4 !py-2 !text-base'
              }
              onChange={(e) => {
                if (isNaN(Number((e.target as HTMLInputElement).value))) {
                  return;
                }
                (e.target as HTMLInputElement).value = (
                  e.target as HTMLInputElement
                ).value.replace(/-|\+/g, '');
                formik.handleChange(e as ChangeEvent<HTMLInputElement>);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.val}
              error={formik.errors.val}
              touched={formik.touched.val}
            />
          </div>
        </div>
        <div className='mt-2 flex flex-wrap gap-x-2'>
          <div>
            <Button
              className='flex items-center'
              disabled={
                formik.isSubmitting || formik.values.val === initialValue
              }
              data-testid='submit-btn'
              onClick={() => formik.submitForm()}
            >
              {formik.isSubmitting && (
                <MiniLoader className='mr-2' color='gray' />
              )}
              <span>{t('Update')}</span>
            </Button>
          </div>
          <div>
            <Button
              styling='outline'
              onClick={closeModal}
              disabled={formik.isSubmitting}
              data-testid='cancel-btn'
            >
              {t('Cancel')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
